import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  portalMe, portalSummary, portalInvoices, portalQuotes, portalQuoteRespond,
  portalMessages, portalSendMessage,
  portalInvoicePdfUrl, portalQuotePdfUrl, portalLogoUrl,
} from '../services/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  FileText, Download, Check, X, MessageSquare, Send, Loader2,
  TrendingUp, AlertCircle, CheckCircle2, Receipt, FileSpreadsheet,
  LayoutDashboard, Lock, Sparkles
} from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(n || 0);
const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return s; }
};

const STATUS_LABEL = {
  paid: 'Pagada',
  pending: 'Pendiente',
  overdue: 'Vencida',
  draft: 'Borrador',
  sent: 'Enviada',
  cancelled: 'Cancelada',
  accepted: 'Aceptado',
  rejected: 'Rechazado',
};
const STATUS_CLS = {
  paid: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  overdue: 'bg-rose-100 text-rose-700',
  sent: 'bg-blue-100 text-blue-700',
  draft: 'bg-stone-100 text-stone-600',
  cancelled: 'bg-stone-100 text-stone-500',
  accepted: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
};

const ClientPortalPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [me, setMe] = useState(null);
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [tab, setTab] = useState('dashboard');
  const [draft, setDraft] = useState('');
  const [respondingTo, setRespondingTo] = useState(null);
  const [respondComment, setRespondComment] = useState('');

  const primary = me?.company?.primary_color || '#FF6B4A';

  const load = useCallback(async () => {
    try {
      const r = await portalMe(token);
      setMe(r.data);
      const [s, inv, qts, msgs] = await Promise.all([
        portalSummary(token),
        portalInvoices(token),
        r.data?.company?.show_quotes !== false ? portalQuotes(token) : Promise.resolve({ data: { items: [] } }),
        r.data?.company?.show_messages !== false ? portalMessages(token) : Promise.resolve({ data: { items: [] } }),
      ]);
      setSummary(s.data);
      setInvoices(inv.data?.items || []);
      setQuotes(qts.data?.items || []);
      setMessages(msgs.data?.items || []);
    } catch (e) {
      setError(e.response?.status === 404 ? 'Enlace inválido o caducado' : 'No se pudo cargar el portal');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const sendMessage = async () => {
    const content = draft.trim();
    if (!content) return;
    try {
      const r = await portalSendMessage(token, content);
      setMessages((prev) => [...prev, r.data]);
      setDraft('');
      toast.success('Mensaje enviado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar el mensaje');
    }
  };

  const submitQuoteResponse = async (action) => {
    if (!respondingTo) return;
    try {
      await portalQuoteRespond(token, respondingTo, action, respondComment);
      toast.success(action === 'accept' ? 'Presupuesto aceptado' : 'Presupuesto rechazado');
      setRespondingTo(null);
      setRespondComment('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar la respuesta');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="w-10 h-10 animate-spin text-[#FF6B4A]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md text-center">
          <Lock className="w-16 h-16 mx-auto text-stone-300 mb-4" />
          <h1 className="text-xl font-bold text-stone-800 mb-2">Acceso no disponible</h1>
          <p className="text-stone-500">{error}</p>
          <p className="text-xs text-stone-400 mt-4">Si tu enlace estaba activo, contacta con la empresa para obtener uno nuevo.</p>
        </div>
      </div>
    );
  }

  const company = me?.company || {};
  const tabs = [
    { key: 'dashboard', label: 'Resumen', icon: LayoutDashboard, show: true },
    { key: 'invoices',  label: 'Facturas', icon: Receipt, show: true },
    { key: 'quotes',    label: 'Presupuestos', icon: FileSpreadsheet, show: company.show_quotes !== false },
    { key: 'messages',  label: 'Mensajes', icon: MessageSquare, show: company.show_messages !== false },
  ].filter((t) => t.show);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="client-portal-page" style={{ '--portal-primary': primary }}>
      {/* Header */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {company.logo_url ? (
              <img src={portalLogoUrl(token)} alt={company.name} className="h-9 max-w-[140px] object-contain" />
            ) : (
              <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold" style={{ background: primary }}>
                {(company.name || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="font-bold text-stone-900 truncate">{company.name || 'Portal del Cliente'}</p>
              <p className="text-xs text-stone-500 truncate">Hola, {me.client.name}</p>
            </div>
          </div>
          <div className="text-xs text-stone-400 hidden sm:block">
            <Sparkles className="w-3 h-3 inline mr-1" />
            Portal seguro
          </div>
        </div>
        {/* Tabs */}
        <nav className="max-w-5xl mx-auto px-4 flex gap-1 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                tab === t.key ? 'text-stone-900 border-current' : 'text-stone-500 border-transparent hover:text-stone-700'
              }`}
              style={tab === t.key ? { color: primary, borderColor: primary } : undefined}
              data-testid={`portal-tab-${t.key}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {tab === 'dashboard' && summary && (
          <div className="space-y-6" data-testid="portal-dashboard">
            {company.welcome_message && (
              <div className="bg-white rounded-xl p-4 border border-stone-100 text-sm text-stone-700 whitespace-pre-wrap">
                {company.welcome_message}
              </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard icon={AlertCircle} label="Pendiente" value={fmt(summary.total_pending)} sub={`${summary.pending_count} factura(s)`} color={primary} highlight />
              <StatCard icon={CheckCircle2} label="Pagado este año" value={fmt(summary.total_paid_year)} color="#10B981" />
              <StatCard icon={TrendingUp} label="Facturado este año" value={fmt(summary.total_billed_year)} color="#3B82F6" />
              <StatCard icon={Receipt} label="Total facturas" value={invoices.length} sub="histórico" color="#A78BFA" />
            </div>

            {summary.upcoming && summary.upcoming.length > 0 && (
              <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
                <div className="p-4 border-b border-stone-100">
                  <h2 className="font-bold text-stone-800">Próximos vencimientos</h2>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-xs uppercase text-stone-500">
                    <tr>
                      <th className="text-left px-4 py-2">Nº</th>
                      <th className="text-left px-4 py-2">Vencimiento</th>
                      <th className="text-right px-4 py-2">Importe</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.upcoming.map((u) => (
                      <tr key={u.id} className={u.overdue ? 'bg-rose-50/50' : ''}>
                        <td className="px-4 py-2 font-mono">{u.invoice_number}</td>
                        <td className="px-4 py-2">{fmtDate(u.due_date)} {u.overdue && <span className="text-rose-600 text-xs ml-1">Vencida</span>}</td>
                        <td className="px-4 py-2 text-right font-bold">{fmt(u.total)}</td>
                        <td className="px-4 py-2 text-right">
                          <a href={portalInvoicePdfUrl(token, u.id)} target="_blank" rel="noreferrer" className="text-xs hover:underline" style={{ color: primary }}>Ver PDF</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'invoices' && (
          <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden" data-testid="portal-invoices">
            {invoices.length === 0 ? (
              <div className="p-10 text-center text-stone-500">No hay facturas aún.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-xs uppercase text-stone-500">
                  <tr>
                    <th className="text-left px-4 py-3">Número</th>
                    <th className="text-left px-4 py-3">Fecha</th>
                    <th className="text-left px-4 py-3">Vencimiento</th>
                    <th className="text-right px-4 py-3">Total</th>
                    <th className="text-center px-4 py-3">Estado</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-t border-stone-100 hover:bg-stone-50">
                      <td className="px-4 py-3 font-mono text-stone-700">{inv.invoice_number}</td>
                      <td className="px-4 py-3">{fmtDate(inv.issue_date)}</td>
                      <td className="px-4 py-3">{fmtDate(inv.due_date)}</td>
                      <td className="px-4 py-3 text-right font-bold">{fmt(inv.total)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${STATUS_CLS[inv.status] || STATUS_CLS.pending}`}>
                          {STATUS_LABEL[inv.status] || inv.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <a
                          href={portalInvoicePdfUrl(token, inv.id)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-medium hover:underline"
                          style={{ color: primary }}
                          data-testid={`portal-invoice-pdf-${inv.id}`}
                        >
                          <Download className="w-3.5 h-3.5" /> PDF
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === 'quotes' && (
          <div className="space-y-3" data-testid="portal-quotes">
            {quotes.length === 0 ? (
              <div className="bg-white rounded-2xl p-10 text-center text-stone-500 border border-stone-100">
                No hay presupuestos aún.
              </div>
            ) : (
              quotes.map((q) => {
                const canRespond = q.status === 'sent' && !q.client_response;
                return (
                  <div key={q.id} className="bg-white rounded-2xl border border-stone-100 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-xs text-stone-500">Presupuesto</p>
                        <p className="font-bold text-stone-800">{q.quote_number}</p>
                        <p className="text-xs text-stone-500 mt-1">Fecha: {fmtDate(q.issue_date)} · Válido hasta: {fmtDate(q.valid_until)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-stone-900">{fmt(q.total)}</p>
                        <span className={`inline-block mt-1 px-2 py-0.5 text-[11px] font-medium rounded-full ${STATUS_CLS[q.status] || STATUS_CLS.sent}`}>
                          {STATUS_LABEL[q.status] || q.status}
                        </span>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <a
                        href={portalQuotePdfUrl(token, q.id)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-stone-200 rounded-lg hover:bg-stone-50"
                      >
                        <Download className="w-3.5 h-3.5" /> Descargar PDF
                      </a>
                      {canRespond && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => { setRespondingTo(q.id); setRespondComment(''); }}
                            className="text-white"
                            style={{ background: primary }}
                            data-testid={`portal-quote-respond-${q.id}`}
                          >
                            Responder
                          </Button>
                        </>
                      )}
                      {q.client_response && (
                        <p className="text-xs text-stone-500 self-center">
                          Respondiste: <strong>{q.client_response === 'accept' ? 'Aceptado' : 'Rechazado'}</strong> el {fmtDate(q.client_response_at)}
                        </p>
                      )}
                    </div>

                    {respondingTo === q.id && (
                      <div className="mt-4 p-3 bg-stone-50 rounded-lg space-y-2" data-testid="portal-quote-respond-form">
                        <Textarea
                          value={respondComment}
                          onChange={(e) => setRespondComment(e.target.value)}
                          placeholder="Comentario opcional (visible para la empresa)"
                          rows={2}
                        />
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="outline" onClick={() => setRespondingTo(null)}>Cancelar</Button>
                          <Button size="sm" variant="outline" onClick={() => submitQuoteResponse('reject')} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="portal-quote-reject-btn">
                            <X className="w-4 h-4 mr-1" /> Rechazar
                          </Button>
                          <Button size="sm" onClick={() => submitQuoteResponse('accept')} className="text-white" style={{ background: primary }} data-testid="portal-quote-accept-btn">
                            <Check className="w-4 h-4 mr-1" /> Aceptar
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {tab === 'messages' && (
          <div className="bg-white rounded-2xl border border-stone-100 flex flex-col h-[calc(100vh-220px)]" data-testid="portal-messages">
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 ? (
                <p className="text-center text-sm text-stone-400 my-8">Aún no hay mensajes. Escribe el primero ↓</p>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_type === 'client' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[75%] px-4 py-2 rounded-2xl ${
                        m.sender_type === 'client'
                          ? 'text-white'
                          : 'bg-stone-100 text-stone-800'
                      }`}
                      style={m.sender_type === 'client' ? { background: primary } : undefined}
                    >
                      <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                      <p className={`text-[10px] mt-1 ${m.sender_type === 'client' ? 'text-white/70' : 'text-stone-400'}`}>
                        {fmtDate(m.created_at)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="p-3 border-t border-stone-100 flex gap-2">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Escribe tu mensaje..."
                rows={2}
                className="flex-1"
                data-testid="portal-message-input"
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); sendMessage(); } }}
              />
              <Button onClick={sendMessage} className="text-white self-end" style={{ background: primary }} data-testid="portal-send-message-btn">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </main>

      <footer className="text-center text-xs text-stone-400 py-6">
        Powered by Facturcal
      </footer>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, color, highlight }) => (
  <div className={`bg-white rounded-2xl border p-4 ${highlight ? 'shadow-md' : ''}`} style={{ borderColor: highlight ? color : '#f5f5f4' }}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-semibold uppercase text-stone-500">{label}</span>
      <Icon className="w-4 h-4" style={{ color }} />
    </div>
    <p className="text-2xl font-bold text-stone-900">{value}</p>
    {sub && <p className="text-xs text-stone-400 mt-1">{sub}</p>}
  </div>
);

export default ClientPortalPage;
